/**
 * school.config.ts
 * ------------------------------------------------------------------
 * Single source of truth for Livingstone Kolobeng College identity.
 *
 * Every surface that shows branding — the login screen, the sidebar,
 * generated PDF reports, and outgoing parent emails — reads from here
 * so the system is unmistakably "LKC's own".
 *
 * Sourced from the official school website (lkc.ac.bw) and crest.
 * ------------------------------------------------------------------
 */

import { BRAND, brandRgb } from '@/lib/brand.palette';

export const SCHOOL = {
  /** Full legal name as it should appear on reports */
  name: 'Livingstone Kolobeng College',
  /** Short name for tight UI spaces */
  shortName: 'LKC',
  /** Cambridge centre number, printed on seating plans, invigilation sheets
   *  and the exam room screen for a Cambridge series (from Al-Nur, 30 Oct 2026).
   *  NOT YET KNOWN for LKC — ask the Examinations Unit and fill it in; until
   *  then a Cambridge printout shows "Centre" with no number. The school's own
   *  exams do not print it. */
  cambridgeCentre: '',
  /** Platform identity — replaces "Reports System" */
  platformName: 'Portal',
  platformNameShort: 'LKC Portal',
  /** Setswana motto from the school crest — "Education is Life" */
  motto: 'Thuto Ke Botshelo',
  mottoTranslation: 'Education is Life',
  tagline: 'Your Label for Academic Success',

  /** Base URL of the deployed app (used in emails) */
  appUrl: 'https://lkcportal.com',
  /** Parent portal login URL */
  parentPortalUrl: 'https://lkcportal.com/?portal=parent',

  /** Contact block (appears in report footers + email signatures) */
  contact: {
    phonePrimary: '(+267) 316 1140',
    phoneSecondary: '75 474 970',
    email: 'principal@lkc.ac.bw',
    website: 'www.lkc.ac.bw',
    addressLines: [
      'Plot 37225, Block 8',
      'Extension 38, Gaborone West',
      'Gaborone, Botswana',
    ],
  },

  social: {
    facebook: 'https://www.facebook.com/lkc.ac.bw/',
    instagram: 'https://www.instagram.com/livingstonekolobeng/',
    twitter: 'https://twitter.com/KolobengCollege',
  },

  /**
   * Verified crest colours — read from `lib/brand.palette.js`, NOT written out
   * here. That module is the single copy of the school's palette; Tailwind
   * reads the same file, so a class name and a generated PDF cannot drift
   * apart. See the comment at the top of brand.palette.js for why it is
   * JavaScript rather than TypeScript.
   *
   * The `rgb` triples jsPDF needs are DERIVED from the hexes rather than kept
   * as a second hand-written list. A colour and its triple used to be two
   * separate edits, and only one of them was ever remembered.
   */
  colors: {
    primary: BRAND.primary,
    primaryDark: BRAND.primaryDark,
    primaryLight: BRAND.primaryLight,
    secondary: BRAND.secondary,
    accent: BRAND.accent,
    ink: BRAND.ink,
    // RGB tuples for jsPDF (it does not accept hex strings everywhere)
    rgb: {
      primary: brandRgb(BRAND.primary),
      primaryDark: brandRgb(BRAND.primaryDark),
      secondary: brandRgb(BRAND.secondary),
      ink: brandRgb(BRAND.ink),
      muted: brandRgb(BRAND.muted),
    },
  },

  /**
   * 2026 academic calendar (from lkc.ac.bw). Term dates are display-only;
   * the system's authoritative term state lives in Firestore `termStatus`.
   */
  academicYearDefault: '2026',
  terms: {
    Term1: { label: 'Term 1', dates: '13 January – 17 April' },
    Term2: { label: 'Term 2', dates: '19 May – 14 August' },
    Term3: { label: 'Term 3', dates: '15 September – 9 December' },
  } as const,

  /** Years selectable in the top bar. Current year only — prevents
   *  accidental data entry in wrong year. */
  selectableYears: [String(new Date().getFullYear())],

  /**
   * Staff clocking: when the working day starts. A first punch after the
   * start is late, by the minutes past it. Leaving late is not counted
   * against anyone, and LKC does not record half days (Oct 2026).
   *
   * Saturday is its own start: Saturday school begins later, and the
   * terminals show staff arriving around 08:00 on Saturdays. Sunday is not
   * a working day. Times are Botswana time (lib/clockingRules).
   */
  clocking: {
    weekdayStart: '07:00',
    saturdayStart: '08:00',
  },
} as const;

export type TermKey = keyof typeof SCHOOL.terms;

/** Human label for a term key, e.g. "Term1" -> "Term 1". */
export function termLabel(term: string): string {
  return (SCHOOL.terms as Record<string, { label: string }>)[term]?.label
    ?? term.replace('Term', 'Term ');
}

/** "Term 1 dates" string for report headers. */
export function termDates(term: string): string {
  return (SCHOOL.terms as Record<string, { dates: string }>)[term]?.dates ?? '';
}

/** The last day of a (year, term), from the same string (from Al-Nur, 31 Oct 2026). */
export function termEndDate(year: string | number, term: string): Date | null {
  const y = Number(year);
  const m = (termDates(term).split(/[–—-]/)[1] ?? '').trim().match(/(\d{1,2})\s+([A-Za-z]+)/);
  if (!y || !m) return null;
  const mon = _MONTHS[m[2].toLowerCase()];
  return mon == null ? null : new Date(y, mon, Number(m[1]));
}

/** The first day of a (year, term), from the term-date string above — where
 *  an exam series' countdown bars start (from Al-Nur, 30 Oct 2026). */
export function termStartDate(year: string | number, term: string): Date | null {
  const y = Number(year);
  const m = (termDates(term).split(/[–—-]/)[0] ?? '').trim().match(/(\d{1,2})\s+([A-Za-z]+)/);
  if (!y || !m) return null;
  const mon = _MONTHS[m[2].toLowerCase()];
  return mon == null ? null : new Date(y, mon, Number(m[1]));
}

/**
 * The "School reopens" date printed on a term's report = the date pupils
 * return, i.e. the START of the NEXT term. Editable here (and overridable at
 * runtime by the Term-Dates settings, see lib/termDates). Falls back to '' so
 * the report simply omits the line when unknown.
 */
export const REOPEN_DATES: Record<string, string> = {
  Term1: '19 May 2026',
  Term2: '14 September 2026',
  Term3: '13 January 2027',
};
export function schoolReopens(term: string): string {
  return REOPEN_DATES[term] ?? '';
}

const _MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

/**
 * Resolve the current term from today's date against the calendar term
 * ranges (display dates double as the authoritative window here). Falls
 * back to the latest term already started, then Term 1.
 */
export function currentTermKey(today: Date = new Date()): TermKey {
  const year = Number(SCHOOL.academicYearDefault) || today.getFullYear();
  const parse = (s: string): Date | null => {
    const m = s.trim().match(/(\d{1,2})\s+([A-Za-z]+)/);
    if (!m) return null;
    const mon = _MONTHS[m[2].toLowerCase()];
    return mon == null ? null : new Date(year, mon, Number(m[1]));
  };
  const keys = Object.keys(SCHOOL.terms) as TermKey[];
  const ranges = keys.map((k) => {
    const [a, b] = (SCHOOL.terms as Record<string, { dates: string }>)[k].dates.split(/[–—-]/);
    return { k, start: parse(a ?? ''), end: parse(b ?? '') };
  });
  const t = today.getTime();
  for (const r of ranges) {
    if (r.start && r.end && t >= r.start.getTime() && t <= r.end.getTime() + 86399999) return r.k;
  }
  const started = ranges.filter((r) => r.start && t >= (r.start as Date).getTime());
  return started.length ? started[started.length - 1].k : keys[0];
}

/** Ordinal position of a term key (Term1 -> 0). */
export function termIndex(term: string): number {
  return (Object.keys(SCHOOL.terms) as string[]).indexOf(term);
}
